import { Configuration as OrderConfiguration } from "@simbelapp/order-sdk";

import { useAuthStore } from "~/store/auth/auth.store";
import { useErrorHandler } from "~/composables/utils/useErrorHandler";
import { readErrorBody, toSdkApiError } from "~/utils/helpers/httpErrors";
import {
  AuthSessionChangedError,
  createAuthenticatedFetch,
  createAuthSessionGuard,
} from "~/utils/helpers/auth/transport";
import { AuthModeEnum } from "~/utils/enums/auth.enums";
import { FetchCredentialsEnum, FetchRedirectEnum } from "~/utils/enums/common.enums";
export type MicroServiceType = "orders" | "orders_reporting";

const ConfigurationMap: Record<MicroServiceType, new (config: any) => any> = {
  orders: OrderConfiguration,
  orders_reporting: OrderConfiguration,
};

function createUnauthenticatedFetch() {
  return async (input: RequestInfo | URL, init?: RequestInit) =>
    await globalThis.fetch(input, {
      ...init,
      credentials: FetchCredentialsEnum.OMIT,
      redirect: init?.redirect === FetchRedirectEnum.MANUAL ? FetchRedirectEnum.MANUAL : FetchRedirectEnum.ERROR,
    });
}

function createApiConfiguration(ms: MicroServiceType, isBearer: boolean) {
  const config = useRuntimeConfig();
  const authStore = useAuthStore();
  const ConfigurationClass = ConfigurationMap[ms];
  const basePath = ms === "orders_reporting" ? config.public.ORDERS_REPORTING_API_URL : config.public.ORDERS_URL;

  return new ConfigurationClass({
    basePath,
    credentials: FetchCredentialsEnum.OMIT,
    fetchApi: isBearer
      ? createAuthenticatedFetch(authStore, globalThis.fetch, {
          allowAuthorizationOverride: (input, init) => {
            const value = input instanceof Request ? input.url : input.toString();
            const url = new URL(value, basePath);
            const method = init?.method ?? (input instanceof Request ? input.method : "GET");
            const headers = new Headers(input instanceof Request ? input.headers : undefined);
            new Headers(init?.headers).forEach((headerValue, key) => headers.set(key, headerValue));
            const expectedAuthorization = authStore.tokens?.refreshToken
              ? `Bearer ${authStore.tokens.refreshToken}`
              : undefined;
            return (
              authStore.mode === AuthModeEnum.LEGACY &&
              headers.get("Authorization") === expectedAuthorization &&
              url.origin === new URL(config.public.ORDERS_URL).origin &&
              url.pathname === "/admin/users" &&
              method.toUpperCase() === "GET"
            );
          },
        })
      : createUnauthenticatedFetch(),
  });
}

function authSessionErrorOf(error: unknown): AuthSessionChangedError | undefined {
  if (error instanceof AuthSessionChangedError) return error;
  if (!error || typeof error !== "object") return undefined;
  const cause = (error as { cause?: unknown }).cause;
  return cause instanceof AuthSessionChangedError ? cause : undefined;
}

function wrapApiMethods<T extends object>(apiInstance: T): T {
  const authStore = useAuthStore();

  return new Proxy(apiInstance, {
    get(target, prop) {
      const originalMethod = target[prop as keyof T];
      if (typeof originalMethod !== "function") return originalMethod;

      return async (...args: any[]) => {
        const guard = createAuthSessionGuard(authStore);
        try {
          try {
            const result = await (originalMethod as any).apply(target, args);
            guard.assertCurrent();
            return result;
          } catch (error: unknown) {
            const authError = authSessionErrorOf(error);
            if (authError) throw authError;
            guard.assertCurrent();

            const sdkError = toSdkApiError(error, await readErrorBody(error));
            guard.assertCurrent();
            const { handleHttpError } = useErrorHandler();
            handleHttpError(sdkError);
            throw sdkError;
          }
        } finally {
          guard.dispose();
        }
      };
    },
  });
}

/** Unproxied client for SDK middleware builders, with the same authenticated transport. */
export async function useRawSDKApi<T extends object>(
  ms: MicroServiceType,
  ApiClass: new (config: any) => T,
  isBearer = true,
): Promise<T> {
  const apiConfig = createApiConfiguration(ms, isBearer);

  return new ApiClass(apiConfig);
}

export async function useSDKApi<T extends object>(
  ms: MicroServiceType,
  ApiClass: new (config: any) => T,
  isBearer = true,
): Promise<T> {
  const apiConfig = createApiConfiguration(ms, isBearer);

  const apiInstance = new ApiClass(apiConfig);

  /**
   * Wraps every API call so a rejection comes back as one `ISdkApiError` — the HTTP status, the
   * parsed body, and the body's own keys copied on top — instead of a bare string whose status was
   * gone. A role-downgrade 409 is routed to the centralized handler on the way out.
   */

  return wrapApiMethods(apiInstance);
}
